import { ReactNode, useState, useEffect, useRef, createContext, useContext } from 'react'
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  Badge,
  Button,
  Switch,
  Snackbar,
  Alert,
  Avatar,
  Tooltip,
} from '@mui/material'
import {
  Notifications,
  AccountCircle,
  Search,
  Menu as MenuIcon,
  Person,
  Settings,
  Logout,
  FilterAlt,
  ConfirmationNumber,
  Chat,
  CreditCard,
  Fullscreen,
  FullscreenExit,
  DarkMode,
} from '@mui/icons-material'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@store/authStore'
import { useUIStore } from '@store/uiStore'
import { Sidebar, SIDEBAR_WIDTH_FULL, SIDEBAR_WIDTH_ICONS, type SidebarCollapseMode } from '@components/navigation/Sidebar'
import { getUnreadCount } from '@api/endpoints/notices'
import { NoticeTypeIds } from '@api/types/notices'
import { chatApi } from '@api/endpoints/chat'
import { useNotificationConnection, useGeneralChatConnection } from '@hooks/useSignalR'
import { startLocationTracking, stopLocationTracking } from '@services/locationTracking'
import { useChatStore } from '@store/chatStore'
import { ChatPopoutManager } from '@features/chat/components/ChatPopoutManager'
import { resolveNotificationMode } from '@api/types/chat'

const SIDEBAR_COLLAPSE_KEY = 'sidebarCollapseMode'

// Build version timestamp - format: yyyyMMddhhmm
const BUILD_VERSION = '202510280147'

// Context for sidebar width
interface SidebarContextType {
  sidebarWidth: number
}

const SidebarContext = createContext<SidebarContextType>({ sidebarWidth: SIDEBAR_WIDTH_FULL })

export const useSidebarWidth = () => useContext(SidebarContext)

interface MainLayoutProps {
  children: ReactNode
}

export function MainLayout({ children }: MainLayoutProps) {
  const { isAuthenticated, user, logout } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<null | HTMLElement>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchDialogOpen, setSearchDialogOpen] = useState(false)
  const [modToast, setModToast] = useState<{ open: boolean; message: string }>({ open: false, message: '' })
  const [infoToast, setInfoToast] = useState<{ open: boolean; message: string }>({ open: false, message: '' })
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md')) // <900px
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm')) // <600px

  // Sidebar collapse state with localStorage persistence
  const [sidebarCollapseMode, setSidebarCollapseMode] = useState<SidebarCollapseMode>(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSE_KEY)
    return (saved ? parseInt(saved, 10) : 2) as SidebarCollapseMode
  })

  const handleSidebarCollapseModeChange = (mode: SidebarCollapseMode) => {
    setSidebarCollapseMode(mode)
    localStorage.setItem(SIDEBAR_COLLAPSE_KEY, mode.toString())

    // Dispatch resize events during and after transition to trigger map resize
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 100)
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 250)
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 400)
  }

  // Get current sidebar width based on collapse mode
  const currentSidebarWidth = sidebarCollapseMode === 0 ? 0 : sidebarCollapseMode === 1 ? SIDEBAR_WIDTH_ICONS : SIDEBAR_WIDTH_FULL

  // Initialize SignalR notification connection globally
  const { isConnected: isNotificationConnected, onNotificationReceived, onNoticeDeleted } = useNotificationConnection()

  // Handle real-time notifications
  useEffect(() => {
    if (!isNotificationConnected) {
      return
    }

    console.log('✅ MainLayout: Setting up global notification listener')

    const unsubscribe = onNotificationReceived((notification) => {
      console.log('🔔 MainLayout: Notification received, refreshing queries')

      // Refresh notification-related queries
      queryClient.invalidateQueries({ queryKey: ['userNotices'] })
      queryClient.invalidateQueries({ queryKey: ['unreadCount'] })
      queryClient.invalidateQueries({ queryKey: ['moderatorApprovals'] })
      // Also refresh conversations — notifications include being added to new conversations
      queryClient.invalidateQueries({ queryKey: ['chatConversations'] })

      // Show toast for moderator approval requests
      const noticeType = (notification?.type || notification?.Type || '').toUpperCase()
      if (noticeType === NoticeTypeIds.MODERATOR_APPROVAL_REQUEST.toUpperCase()) {
        const title = notification?.title || notification?.Title || ''
        const msg = notification?.quickMessage || notification?.QuickMessage || 'A participant is waiting for moderator approval.'
        setModToast({ open: true, message: title ? `${title} — ${msg}` : msg })
      }

      // Show toast and refresh invites for session invite responses (accepted/declined)
      const title = notification?.title || notification?.Title || ''
      if (title.toLowerCase().includes('session invite declined') || title.toLowerCase().includes('session invite accepted')) {
        const msg = notification?.message || notification?.quickMessage || title
        setInfoToast({ open: true, message: msg })
        // Refresh session invite and participant lists
        queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'sessions' && (q.queryKey[1] === 'invites' || q.queryKey[1] === 'participants') })
      }
    })

    return unsubscribe
  }, [isNotificationConnected, onNotificationReceived, queryClient])

  // Handle real-time notice deletions
  useEffect(() => {
    if (!isNotificationConnected) return

    const unsubscribe = onNoticeDeleted((event) => {
      const noticeId = event?.NoticeId || event?.noticeId
      if (!noticeId) return

      // Optimistically remove the notice from the cached list
      queryClient.setQueryData<any[]>(['userNotices'], (old) => {
        if (!old) return old
        return old.filter((n: any) => n.id !== noticeId)
      })
      queryClient.invalidateQueries({ queryKey: ['unreadCount'] })
    })

    return unsubscribe
  }, [isNotificationConnected, onNoticeDeleted, queryClient])

  // Initialize SignalR chat connection globally for unread badge updates
  const { isConnected: isChatConnected, onConversationUpdateSignal, onUserJoinedConversation, joinConversation: joinChatRoom, leaveConversation: leaveChatRoom } = useGeneralChatConnection()

  // Fetch conversations for initial unread counts (before room joins reset them)
  const { data: globalConversations } = useQuery({
    queryKey: ['chatConversations'],
    queryFn: chatApi.getConversations,
    enabled: isAuthenticated,
  })

  // Compute unread badge directly from query data
  const unreadChatCount = globalConversations?.reduce(
    (sum: number, c: any) => sum + (c.unreadMessageCount || 0), 0
  ) || 0

  // Join all conversation rooms so we receive SignalR events
  const joinedRoomsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!isChatConnected || !globalConversations) return

    const currentIds = new Set(globalConversations.map((c: any) => c.id))

    currentIds.forEach((id) => {
      if (!joinedRoomsRef.current.has(id)) {
        joinChatRoom(id)
        joinedRoomsRef.current.add(id)
      }
    })

    joinedRoomsRef.current.forEach((id) => {
      if (!currentIds.has(id)) {
        leaveChatRoom(id)
        joinedRoomsRef.current.delete(id)
      }
    })
  }, [isChatConnected, globalConversations, joinChatRoom, leaveChatRoom])

  // Invalidate conversations query when real-time chat messages arrive
  useEffect(() => {
    if (!isChatConnected) return

    const unsub = onConversationUpdateSignal?.((signal) => {
      const isOwnMessage = signal.senderId === user?.id || signal.senderId === user?.userId
      if (signal.updateType === 'ParticipantLeft') {
        queryClient.invalidateQueries({ queryKey: ['chatConversations'] })
        return
      }
      if (signal.updateType === 'NewMessage') {
        // Always refresh conversations — this handles both existing and brand-new conversations
        queryClient.invalidateQueries({ queryKey: ['chatConversations'] })

        // Nothing more to do for own messages
        if (isOwnMessage) return

        // Auto-open popout if the conversation's notification mode is AutoOpen
        const chatState = useChatStore.getState()
        // Skip if user is on the chat page (use window.location to avoid stale closure)
        if (window.location.pathname.startsWith('/chat')) {
          return
        }
        // Read conversations from the query cache at signal time to avoid stale closure
        const cachedConversations = queryClient.getQueryData<any[]>(['chatConversations'])
        const conversation = cachedConversations?.find((c: any) => c.id === signal.conversationId)
        if (conversation) {
          const mode = resolveNotificationMode(conversation.notificationMode)
          console.log('[MainLayout] Auto-open check:', { conversationId: signal.conversationId, mode, notificationMode: conversation.notificationMode })
          if (mode === 'AutoOpen') {
            chatState.addPopout(signal.conversationId)
          }
        } else {
          console.log('[MainLayout] New conversation not yet in cache, will appear after refresh:', signal.conversationId)
        }
      }
    })

    return () => { unsub?.() }
  }, [isChatConnected, onConversationUpdateSignal, queryClient, user?.id, user?.userId])

  // Handle being added to a new conversation (e.g., someone created a group chat including this user)
  useEffect(() => {
    if (!isChatConnected) return

    const unsub = onUserJoinedConversation?.((event) => {
      console.log('[MainLayout] UserJoinedConversation:', event)
      // Refresh conversations so the new one appears in the list and room-join effect fires
      queryClient.invalidateQueries({ queryKey: ['chatConversations'] })
      // Eagerly join the room so signals for it arrive immediately
      if (event.conversationId) {
        joinChatRoom(event.conversationId)
        joinedRoomsRef.current.add(event.conversationId)
      }
    })

    return () => { unsub?.() }
  }, [isChatConnected, onUserJoinedConversation, queryClient, joinChatRoom])

  // Start location tracking when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      console.log('[MainLayout] Starting location tracking for authenticated user')
      startLocationTracking()
    }

    return () => {
      stopLocationTracking()
    }
  }, [isAuthenticated])

  // Fetch unread notification count
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['unreadCount'],
    queryFn: getUnreadCount,
    enabled: isAuthenticated,
    refetchInterval: 60000, // Refetch every minute
  })

  const handleChatClick = () => {
    navigate('/chat')
  }

  // Clear search query when navigating away from search results page or to advanced search
  useEffect(() => {
    if (!location.pathname.startsWith('/search') || location.pathname === '/search/advanced') {
      setSearchQuery('')
    }
  }, [location.pathname])

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleAccountMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAccountMenuAnchor(event.currentTarget)
  }

  const handleAccountMenuClose = () => {
    setAccountMenuAnchor(null)
  }

  const handleProfileClick = () => {
    handleAccountMenuClose()
    navigate('/profile')
  }

  const handleSettingsClick = () => {
    handleAccountMenuClose()
    navigate('/settings')
  }

  const handleSupportClick = () => {
    handleAccountMenuClose()
    navigate('/support/tickets')
  }

  const handleSubscriptionClick = () => {
    handleAccountMenuClose()
    navigate('/subscription')
  }

  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement)

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onFsChange)
    return () => document.removeEventListener('fullscreenchange', onFsChange)
  }, [])

  const handleFullscreenToggle = () => {
    handleAccountMenuClose()
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      document.documentElement.requestFullscreen()
    }
  }

  const { themeMode, toggleThemeMode } = useUIStore()

  const handleLogout = () => {
    handleAccountMenuClose()
    logout()
  }

  // Debounced search handler
  useEffect(() => {
    if (!searchQuery.trim()) {
      return
    }

    const timer = setTimeout(() => {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery, navigate])

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`)
      setSearchDialogOpen(false)
      setSearchQuery('')
    }
  }

  const handleSearchIconClick = () => {
    if (isSmallScreen) {
      setSearchDialogOpen(true)
    } else {
      navigate('/search/advanced')
    }
  }

  const handleNotificationsClick = () => {
    navigate('/notifications')
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', width: '100%' }}>
      {/* Header spans full width */}
      <AppBar
        position="sticky"
        sx={{
          top: 0,
          backgroundColor: 'primary.main',
          color: theme.palette.mode === 'dark' ? 'background.default' : 'white',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexShrink: 0,
        }}
      >
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            {/* Left: Hamburger Menu + Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Hamburger menu - always visible, toggles sidebar */}
              <IconButton
                color="inherit"
                edge="start"
                onClick={isMobile ? handleDrawerToggle : () => {
                  // Cycle through modes: 2 -> 1 -> 0 -> 2
                  const nextMode = sidebarCollapseMode === 2 ? 1 : sidebarCollapseMode === 1 ? 0 : 2
                  handleSidebarCollapseModeChange(nextMode as SidebarCollapseMode)
                }}
              >
                <MenuIcon />
              </IconButton>

              <Typography
                variant="h5"
                sx={{
                  fontFamily: '"Brush Script MT", cursive',
                  color: 'inherit',
                  fontWeight: 'bold',
                  fontSize: { xs: '1.2rem', sm: '1.5rem' },
                }}
              >
                Hidden Adventures
              </Typography>
            </Box>

            {/* Right: Search Bar + User Actions */}
            <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
              {/* Search Bar - show on larger screens */}
              <TextField
                  placeholder="Search adventures, users, groups..."
                  size="small"
                  fullWidth
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyPress={handleSearchKeyPress}
                  onBlur={() => setSearchQuery('')}
                  sx={{
                    display: { xs: 'none', sm: 'block' },
                    flex: 1,
                    maxWidth: { sm: 300, md: 450, lg: 600 },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(26, 26, 46, 0.2)' : 'rgba(255, 255, 255, 0.12)',
                      borderRadius: '24px',
                      color: 'inherit',
                      '& fieldset': {
                        borderColor: 'transparent',
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.mode === 'dark' ? 'rgba(26, 26, 46, 0.4)' : 'rgba(255, 255, 255, 0.3)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.mode === 'dark' ? theme.palette.background.default : 'white',
                      },
                    },
                    '& .MuiInputBase-input': {
                      padding: '8.5px 14px',
                      '&::placeholder': {
                        color: 'inherit',
                        opacity: 0.6,
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: 'inherit', opacity: 0.6 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => navigate('/search/advanced')}
                          sx={{
                            color: 'inherit',
                            opacity: 0.6,
                            '&:hover': {
                              opacity: 1,
                              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(26, 26, 46, 0.15)' : 'rgba(255, 255, 255, 0.1)',
                            }
                          }}
                        >
                          <FilterAlt fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

              {/* User Actions */}
              {/* Search Icon - show on small screens, hide on larger screens where search bar is visible */}
              <IconButton
                color="inherit"
                size="small"
                onClick={handleSearchIconClick}
                sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
              >
                <Search />
              </IconButton>
              {/* Chat Icon */}
              <IconButton
                color="inherit"
                size="small"
                onClick={handleChatClick}
              >
                <Badge badgeContent={unreadChatCount} color="error">
                  <Chat />
                </Badge>
              </IconButton>
              {/* Notifications Icon - always visible */}
              <IconButton
                color="inherit"
                size="small"
                onClick={handleNotificationsClick}
              >
                <Badge badgeContent={unreadCount} color="error">
                  <Notifications />
                </Badge>
              </IconButton>
              <Tooltip title={user?.displayName || 'Account'}>
                <IconButton color="inherit" size="small" onClick={handleAccountMenuOpen}>
                  {user?.avatarUrl || user?.avatar ? (
                    <Avatar
                      src={user.avatarUrl || user.avatar}
                      alt={user.displayName}
                      sx={{ width: 24, height: 24 }}
                    />
                  ) : (
                    <AccountCircle />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>
      </AppBar>

      {/* Account Menu */}
      <Menu
        anchorEl={accountMenuAnchor}
        open={Boolean(accountMenuAnchor)}
        onClose={handleAccountMenuClose}
        onClick={handleAccountMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              minWidth: 200,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
      >
        <MenuItem onClick={handleProfileClick}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleSettingsClick}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleSubscriptionClick}>
          <ListItemIcon>
            <CreditCard fontSize="small" />
          </ListItemIcon>
          <ListItemText>Subscription</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleSupportClick}>
          <ListItemIcon>
            <ConfirmationNumber fontSize="small" />
          </ListItemIcon>
          <ListItemText>Support</ListItemText>
        </MenuItem>
        <MenuItem onClick={(e) => { e.stopPropagation(); toggleThemeMode(); }}>
          <ListItemIcon>
            <DarkMode fontSize="small" />
          </ListItemIcon>
          <ListItemText>Dark Mode</ListItemText>
          <Switch
            size="small"
            checked={themeMode === 'dark'}
            onChange={toggleThemeMode}
            onClick={(e) => e.stopPropagation()}
            sx={{ ml: 1 }}
          />
        </MenuItem>
        <MenuItem onClick={handleFullscreenToggle}>
          <ListItemIcon>
            {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
        <Divider />
        <Box sx={{ px: 2, py: 1, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Version {BUILD_VERSION}
          </Typography>
        </Box>
      </Menu>

      {/* Content area with sidebar */}
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden', width: '100%' }}>
        <Sidebar
          mobileOpen={mobileOpen}
          onDrawerToggle={handleDrawerToggle}
          isMobile={isMobile}
          collapseMode={sidebarCollapseMode}
          onCollapseModeChange={handleSidebarCollapseModeChange}
        />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            flex: 1,
            minWidth: 0,
            backgroundColor: 'background.default',
            overflow: 'auto',
            position: 'relative',
            transition: 'all 0.2s ease-in-out',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <SidebarContext.Provider value={{ sidebarWidth: currentSidebarWidth }}>
            {children}
          </SidebarContext.Provider>
        </Box>
      </Box>

      {/* Chat Popouts */}
      <ChatPopoutManager />

      {/* Mobile Search Dialog */}
      <Dialog
        open={searchDialogOpen}
        onClose={() => setSearchDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Search Adventures</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            placeholder="Search adventures, users, groups..."
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyPress={handleSearchKeyPress}
            sx={{ mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ mt: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                navigate('/search/advanced')
                setSearchDialogOpen(false)
              }}
            >
              Advanced Search
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Moderator approval toast */}
      <Snackbar
        open={modToast.open}
        autoHideDuration={10000}
        onClose={() => setModToast({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setModToast({ open: false, message: '' })}
          severity="warning"
          variant="filled"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setModToast({ open: false, message: '' })
                navigate('/moderator-approvals')
              }}
            >
              Review
            </Button>
          }
          sx={{ width: '100%' }}
        >
          {modToast.message}
        </Alert>
      </Snackbar>

      {/* Info toast (session invite responses, etc.) */}
      <Snackbar
        open={infoToast.open}
        autoHideDuration={5000}
        onClose={() => setInfoToast({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setInfoToast({ open: false, message: '' })}
          severity="info"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {infoToast.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
